import React, { useCallback, useEffect } from 'react';
import { CoreDropdown } from '@komodo-design-system/komodo-components-react';
import { MetadataElement, MetadataElementList } from './SingleUploadModel';
import './SingleUploadContent.scss';
import { ThunkDispatch } from 'redux-thunk';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { singleUploadActions } from 'actions/singleUpload';
import { isEmpty } from 'lodash-es';
import { dropdownOptionsAction } from 'actions/dropdownOptions';
import { DROPDOWN_OPTIONS_UPDATE } from 'constants/actionTypes';
import { getAllElementsByMetaTableId } from 'utils/utils';
import AlertBox from 'componentsV2/SharedAlertBox/AlertBox/AlertBox';

interface DropdownFieldProps {
  element: MetadataElement;
  rowIndex?: number | null; // Optional rowIndex prop
  assetId?: string; // Optional prop for assetId
}

const DropdownFieldWrapper: React.FC<DropdownFieldProps> = ({
  element,
  rowIndex,
  assetId
}) => {
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const [alert, setAlert] = React.useState({
    show: false,
    message: '',
  });
  const targetsiteErpMappings = useSelector((state: any) => state.targetsiteErpMappingReducer?.targetSiteERPMap) || {};
  const singleUploadPayload = useSelector(
    (state: any) => state.singleUpload.payload
  );
  const [dropdownOptions, setDropdownOptions] = React.useState<any[]>([]);
  const { eventType } = useSelector(
    (state: any) => state.submitValidationReducer
  );
  const assetTypes = useSelector(
    (state: any) => state.assetTypeReducer?.assetTypes
  );
  const dropdownOptionsState = useSelector(
    (state: any) => state.dropdownOptionsReducer?.dropdownOptions
  );
  // call the api based on element.domain_id
  const fetchDropdownOptions = async () => {
    const elementDomainId = element.domain_id as string;
    let response: any = {};
    // check for data in store
    try {
      dispatch({ type: 'LOADING SUCCESS', payload: true });
      let assetTypeDefaults =
        assetTypes?.metadata_collection?.DEFAULTS?.[
          singleUploadPayload.metadata_model_id
        ] || {};
      if (assetTypeDefaults[element.field_id]) {
        const defaultValues = assetTypeDefaults[element.field_id];
        const mappedValues = defaultValues.map((item: any) => ({
          description: item.display_value,
          id: item.id,
          value: item.display_value
        }));
        dispatch(
          dropdownOptionsAction.updateDropdownOptions(
            DROPDOWN_OPTIONS_UPDATE,
            mappedValues || [],
            elementDomainId?.toLowerCase()
          )
        );
        response = { [elementDomainId?.toLowerCase()]: mappedValues || [] };
      } else {
        const existingOptions = !isEmpty(dropdownOptionsState)
          ? dropdownOptionsState[elementDomainId?.toLowerCase()]
          : null;
        response = existingOptions
          ? { [elementDomainId?.toLowerCase()]: existingOptions }
          : await singleUploadActions.fetchDropdownValues(
              element.domain_id as string
          );
        dispatch({ type: 'LOADING SUCCESS', payload: false });
        if (isEmpty(existingOptions)) {
          // Update the dropdown options in the store
          dispatch(
            dropdownOptionsAction.updateDropdownOptions(
              DROPDOWN_OPTIONS_UPDATE,
              response[elementDomainId?.toLowerCase()] || [],
              elementDomainId?.toLowerCase()
            )
          );
        }
      }
      const data =
        response[element.domain_id?.toString().toLowerCase() as string];
      if (data) {
        //change description to label
        const formattedData = data.map((item: any) => ({
          id: item.id,
          label: item.description, // Use display_value as label if it exists, otherwise use value
          // label:
          //   item.description === item.value
          //     ? item.value
          //     : `${item.description} [ ${item.value} ]` // Use description as label if it exists, otherwise use value
        }));
        // default value for the dropdown considering single value
        if (assetTypeDefaults[element.field_id]) {
          const defaultValue = assetTypeDefaults[element.field_id];
          const defaultOption = defaultValue
            .map((item: any) => {
              const foundOption = formattedData.find(
                (option: any) => option.id === item.id
              );
              return foundOption
                ? { id: foundOption.id, label: foundOption.label }
                : null;
            })
            .filter((item: any) => item !== null);
          if (defaultOption && isEmpty(assetId)) {
            handleDropdownChange(defaultOption[0]);
          }
        }
        // add a default empty object to the dropdown options
        // if (
        //   !formattedData.some(
        //     (option: any) =>
        //       option.id === 'None' && option.label === 'None'
        //   ) && element.is_required !== 'Y'
        // ) {
        //   formattedData.unshift({ id: '', label: '' });
        // }
        // Set the dropdown options
        setDropdownOptions(formattedData);
      } else {
        setDropdownOptions([]);
      }
    } catch (error) {
      setDropdownOptions([]);
    }
  };
  useEffect(() => {
    if (element.domain_id) {
      fetchDropdownOptions();
    }
  }, [element.domain_id]);

  const clearRow = (elementWithMetaTableId: any) => {
    const fieldIds = elementWithMetaTableId.map(
      (el: MetadataElement) => el.field_id
    );
    // Clear the values of all the elements with same meta_table_id
    const updatedPayload = singleUploadPayload.metadata_collection.metadata_element_list.find(
      (el: MetadataElement) => el.name === element.name
    );

    updatedPayload.metadata_element_list.forEach((el: MetadataElement) => {
      if (el.meta_table_id === element.meta_table_id) {
        if (fieldIds.includes(el.field_id) && fieldIds.indexOf(el.field_id) !== 0) {
          // Clear the values of the element
          if (el.values) {
            el.values[rowIndex as number] = { id: '', display_value: '' };
          }
        }
      }
    }
    );
    // Dispatch the updated payload to the Redux store
    const updatedSingleUploadPayload = {
      ...singleUploadPayload,
      metadata_collection: {
        ...singleUploadPayload.metadata_collection,
        metadata_element_list: singleUploadPayload.metadata_collection.metadata_element_list.map(
          (el: MetadataElementList) =>
            el.name === updatedPayload.name ? updatedPayload : el
        )
      }
    };
    dispatch(
      singleUploadActions.updateSingleUploadPayload(
        updatedSingleUploadPayload
      )
    );
  };

  // Function to get all elements with the same meta_table_id and clear their values
  const getAllElementsWithSameMetaTableId = (
    payload: any,
    metaTableId: string
  ) => {
    const elementWithMetaTableId = getAllElementsByMetaTableId(payload, metaTableId);
    //get the index of the element with same meta_table_id
    const index = elementWithMetaTableId.findIndex(
      (e: MetadataElement) => e.field_id === element.field_id
    );
    // then clear the values of all the elements with same meta_table_id
    if (index === 0 && elementWithMetaTableId.length > 1 && element.meta_table_id!=='TFS_PRODUCT_IDENTIFIERS') {
      //clear the values of all the elements with same meta_table_id
      clearRow(elementWithMetaTableId);
    }
  };

  // Handle dropdown value change
  const handleDropdownChange = (value: any) => {
    const updatedPayload = singleUploadPayload.metadata_collection.metadata_element_list.find(
      (el: MetadataElement) => el.name === element.name
    );

    if (!updatedPayload) {
      console.warn(
        `Element with name "${element.name}" not found in metadata_element_list.`
      );
      return;
    }

    // Update the specific field's value
    if (updatedPayload) {
      // Update the specific field's value
      updatedPayload.metadata_element_list.forEach((el: MetadataElement) => {
        if (el.field_id === element.field_id) {
          if (el.value && typeof el.value === 'object') {
            if (el.value.id === value.id && el.is_required !== 'Y') {
              // If the value is already present, do not update
              el.value = { id: '', display_value: '' };
              return;
            }

            // Update the id and display_value properties if value is an object
            el.value = { id: value.id, display_value: value.label };
          } else if (el.values && Array.isArray(el.values)) {
            // check in values array if the value already exists
            const isPrevValueExists = typeof rowIndex === 'number' && el.values[rowIndex]?.id === value.id;
            if (isPrevValueExists && el.is_required !== 'Y') {
              // If the value already exists and is not required, do not update
              if (typeof rowIndex === 'number') {
                el.values[rowIndex] = { id: '', display_value: '' };
              }
              return;
            }
            if (
              el.values.length === 1 &&
              el.values[0]?.id === '' &&
              el.values[0]?.display_value === ''
            ) {
              // If the array has only one element and it's empty, replace it with the new value
              el.values[0] = { id: value.id, display_value: value.label };
              // find the element with same meta_table_id and from singleUploadPayload
              // if value in first element changes, clear the values of all the elements with same meta_table_id
              if (element.is_tabular === 'Y') {
                if (el.meta_table_id) {
                  getAllElementsWithSameMetaTableId(singleUploadPayload, el.meta_table_id);
                }
              }
            } else {
              // Otherwise, add the new value to the array
              if (typeof rowIndex === 'number') {
                // ensure all the rowIndex before the current rowIndex are empty
                for (let i = 0; i < rowIndex; i++) {
                  if (!el.values[i]) {
                    el.values[i] = { id: '', display_value: '' }; // Replace with default object
                  }
                }
                // check if el.values has id is equal to value.id
                const existingIndex = el.values.findIndex(
                  (v: any) => v.id === value.id
                );
                if(el.is_tabular === 'Y' && el.meta_table_id) {
                  const elementsWithSameMetaTableId =  getAllElementsByMetaTableId(singleUploadPayload, el.meta_table_id);
                  if( elementsWithSameMetaTableId.length === 1){
                    if (existingIndex === -1) {
                      // Update the specific rowIndex with the new value
                      el.values[rowIndex] = {
                        id: value.id,
                        display_value: value.label
                      };
                    }
                    else{
                      el.values[rowIndex] = {
                        id: '',
                        display_value: ''
                      };
                      setAlert({
                        show: true,
                        message: 'This value has already been selected. Please choose a different option.',
                      });
                      // If the value is not found, do not update
                    }
                  }
                  else{
                    el.values[rowIndex] = {
                      id: value.id,
                      display_value: value.label
                    };
                    getAllElementsWithSameMetaTableId(singleUploadPayload, el.meta_table_id);
                  }
                }
              } else {
                console.log('Invalid rowIndex:', rowIndex);
              }
            }
          }
        }
      });

      // Dispatch the updated payload to the Redux store
      const updatedSingleUploadPayload = {
        ...singleUploadPayload,
        metadata_collection: {
          ...singleUploadPayload.metadata_collection,
          metadata_element_list: singleUploadPayload.metadata_collection.metadata_element_list.map(
            (el: MetadataElementList) =>
              el.name === updatedPayload.name ? updatedPayload : el
          )
        }
      };

      dispatch(
        singleUploadActions.updateSingleUploadPayload(
          updatedSingleUploadPayload
        )
      );
    }
  };

  let selectedValue: any = null; // Initialize selectedValue
  if (element?.values?.length ?? 0 > 1) {
    selectedValue = singleUploadPayload?.metadata_collection?.metadata_element_list
      ?.find((el: MetadataElement) => el.name === element.name)
      ?.metadata_element_list.find(
        (el: MetadataElement) => el.field_id === element.field_id
      )?.values[rowIndex as number]?.display_value;
  } else {
    selectedValue = singleUploadPayload?.metadata_collection?.metadata_element_list
      ?.find((el: MetadataElement) => el.name === element.name)
      ?.metadata_element_list.find(
        (el: MetadataElement) => el.field_id === element.field_id
      )?.value?.display_value;
  }

  // error message
  const getErrorMessage = () => {
    if (
      eventType === 'validate' &&
      !selectedValue &&
      element.is_required === 'Y'
    ) {
      return 'This field is required';
    }
    return '';
  };

  // for prouduct identifiers
  const isProductIdentifiers =
    singleUploadPayload?.metadata_collection?.metadata_element_list?.findIndex(
      (obj: any) => obj.id === 'TFS.CATEGORY.PRODUCT_IDENTIFIERS'
    ) > -1;
  const checkboxValues = isProductIdentifiers && singleUploadPayload?.metadata_collection?.metadata_element_list
    ?.find((obj: any) => obj.id === 'TFS.CATEGORY.PRODUCT_IDENTIFIERS')
    ?.metadata_element_list?.find(
      (obj: any) => obj.field_id === 'TFS.FIELD.IS_CUSTOM'
    )?.values;
  const isCustomField = checkboxValues[rowIndex as number]?.id;

  const getIsDisabled = () => {
    if (element.is_editable === 'Y') {
      return false;
    } else if (isProductIdentifiers && isCustomField === 'Y') {
      return false;
    }
    return true;
  };

  // Helper function to get unique items from target site ERP mappings
  const getUniqueTargetSites = useCallback(() => {
    return targetsiteErpMappings?.tfs_erp_target_site_lu?.reduce((unique: any[], item: any) => {
      const exists = unique.find(u => u.target_site_id === item.target_site_id);
      if (!exists) {
        unique.push({
          target_site_id: item.target_site_id,
          description: item.target_site_description || item.description || item.target_site_id
        });
      }
      return unique;
    }, []) || [];
  }, [targetsiteErpMappings]);

  // Helper function to get unique ERP types
  const getUniqueERPTypes = useCallback(() => {
    return targetsiteErpMappings?.tfs_erp_target_site_lu?.reduce((unique: any[], item: any) => {
      const exists = unique.find(u => u.erp_id === item.erp_id);
      if (!exists) {
        unique.push({
          erp_id: item.erp_id,
          description: item.erp_type || item.description || item.erp_id
        });
      }
      return unique;
    }, []) || [];
  }, [targetsiteErpMappings]);

  // Helper function to get selected target site ID
  const getSelectedTargetSiteID = useCallback(() => {
    return singleUploadPayload?.metadata_collection?.metadata_element_list
      ?.find((el: MetadataElement) => el.name === element.name)
      ?.metadata_element_list.find(
        (el: MetadataElement) => el.field_id === 'TFS.FIELD.CAT_ERP'
      )
      ?.values[rowIndex as number]?.id?.split(' ')[0];
  }, [singleUploadPayload, element.name, rowIndex]);

  // Helper function to format options for dropdown
  const formatDropdownOptions = useCallback((items: any[], idKey: string, labelKey: string) => {
    return items.map((item: any) => ({
      id: item[idKey],
      label: item[labelKey]
    }));
  }, []);

  const updateERPMap = useCallback(() => {
    // Handle TFS.FIELD.CAT_ERP field - show unique target sites
    if (element.field_id === 'TFS.FIELD.CAT_ERP' && isCustomField) {
      const uniqueTargetSites = getUniqueTargetSites();
      const options = formatDropdownOptions(uniqueTargetSites, 'target_site_id', 'description');
      setDropdownOptions(options);
      return;
    }

    // Handle TFS.FIELD.ERP field - show ERP types based on selected target site
    if (element.field_id === 'TFS.FIELD.ERP' && isCustomField) {
      const selectedTargetSiteID = getSelectedTargetSiteID();
      
      // If no target site is selected, show all unique ERP types
      if (!selectedTargetSiteID) {
        const uniqueERPTypes = getUniqueERPTypes();
        const options = formatDropdownOptions(uniqueERPTypes, 'erp_id', 'description');
        setDropdownOptions(options);
        return;
      }

      // Filter ERP types by selected target site
      const filteredERPTypes = targetsiteErpMappings?.tfs_erp_target_site_lu
        ?.filter(({ target_site_id }: { target_site_id: string }) => target_site_id === selectedTargetSiteID)
        ?.map((erp: any) => ({ id: erp.erp_id, label: erp.erp_type })) || [];
      
      setDropdownOptions(filteredERPTypes);
    }
  }, [element, isCustomField, targetsiteErpMappings, formatDropdownOptions, getSelectedTargetSiteID, getUniqueERPTypes, getUniqueTargetSites]);

  useEffect(() => {
    // Fetch dropdown options when the component mounts or when the element changes

    if (element.domain_id) {
      updateERPMap();
    }
  }, [singleUploadPayload]);

  const menuSize = (element.field_id === 'TFS.FIELD.ERP' || element.field_id === 'TFS.FIELD.CAT_ERP') ? 2 : 4; // Set menu size based on field_id

  // Render a single dropdown
  const renderDropdown = () => (
    <CoreDropdown
      filterable
      wrap
      emptylabel="No options found"
      placeholder="Select"
      functionality="single-select"
      className="dropdown-field"
      size="regular"
      customkey="label"
      options={dropdownOptions}
      selected={isEmpty(selectedValue) ? [] : [selectedValue]}
      disabled={getIsDisabled()}
      onSelectionChanged={(event) => {
        const selectedOption = event.detail.option;
        handleDropdownChange(selectedOption);
      }}
      menusize={menuSize}
      data-testid="dropdown"
      error={getErrorMessage() !== ''}
      errormessage={getErrorMessage()}
    />
  );

  return <>{renderDropdown()}
    <div style={{wordBreak: 'normal'}}>
      {alert.show && (
        <AlertBox
          isModalOpen={alert.show}
          message={alert.message}
          handleClose={() => setAlert({ show: false, message: '' })}
        />)
      }
    </div>
  </>;
};

export default DropdownFieldWrapper;
